import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { from, Observable, Observer, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { GraphqlService } from 'src/app/graphql/graphql.service';
import { AddressSuggestion } from 'src/app/shared/models/address-suggestion';

@Component({
	selector: 'pk-broker-address-autocomplete',
	templateUrl: './address-autocomplete.component.html',
	standalone: false,
})
export class AddressAutocompleteComponent {
	@Input() label = 'Address';
	@Input() labelClass = '';
	@Input() required = false;
	@Input() companyId: string;
	@Input() countryId: number;
	@Input() zipCode: string;
	@Input() invalid = false;
	@Input() invalidText = 'Address is required.';
	@Input() helperText = 'Choose a match to fill the address.';
	@Input() allowManualEntry = true;
	// e.g. while the parent looks up the city and state for a chosen address's zip code
	@Input() disabled = false;
	// follows the parent's address until the user edits the text, since the parent can fill fields after a selection
	// (e.g. city and state arrive after a zip code lookup) or patch its form after this component is created
	@Input() set initialText(value: string) {
		if (value && (!this.query || this.query === this.selectedLabel)) {
			this.query = value;
			this.selectedLabel = value;
		}
	}

	@Output() selected = new EventEmitter<AddressSuggestion>();
	@Output() cleared = new EventEmitter<void>();
	@Output() manualEntry = new EventEmitter<void>();

	@ViewChild('addressInput') addressInput: ElementRef<HTMLInputElement>;

	public inputId = `address-autocomplete-${uuid()}`;
	public query = '';
	public minLength = 3;
	public loading = false;
	public suggestions: AddressSuggestion[] = null;
	public dataSource: Observable<AddressSuggestion[]>;

	private selectedLabel: string = null;
	private lastSearch = '';
	// while set, the typeahead lists the units of a chosen building instead of searching the typed text
	private expansion: { label: string; search: string; selected: string } = null;

	constructor(private graphqlService: GraphqlService) {
		this.dataSource = new Observable((observer: Observer<string>) => {
			observer.next(this.query);
		}).pipe(switchMap((q: string) => this.getAddressTypeahead$(q)));
	}

	// typing would search the building's text instead of narrowing its units, so the text is locked while they're listed
	get isExpanding(): boolean {
		return !!this.expansion;
	}

	public stopExpanding(): void {
		this.expansion = null;
	}

	get showNoMatch(): boolean {
		return this.query?.trim().length >= this.minLength && !this.loading && !!this.suggestions && !this.suggestions.length;
	}

	public onQueryChange(value: string): void {
		if (this.expansion && value !== this.expansion.label) {
			this.expansion = null;
		}
		if (this.selectedLabel !== null && value !== this.selectedLabel) {
			this.selectedLabel = null;
			this.cleared.emit();
		}
	}

	public onSelect(match: TypeaheadMatch): void {
		const item = match.item as AddressSuggestion;
		if (item.needsExpansion) {
			this.expandSuggestion(item);
			return;
		}
		this.expansion = null;
		this.suggestions = null;
		this.query = item.typeaheadLabel;
		this.selectedLabel = this.query;
		this.selected.emit(item);
	}

	private expandSuggestion(item: AddressSuggestion): void {
		this.query = item.typeaheadLabel;
		this.expansion = { label: this.query, search: this.lastSearch, selected: item.expandSelection };
		// let the input render the new text, then re-trigger the typeahead so it searches for the units
		setTimeout(() => {
			const input = this.addressInput?.nativeElement;
			if (input) {
				input.focus();
				input.dispatchEvent(new Event('input'));
			}
		});
	}

	private getAddressTypeahead$(query: string): Observable<AddressSuggestion[]> {
		if (!query || query.trim().length < this.minLength || query === this.selectedLabel) {
			return of([]);
		}
		const expansion = this.expansion?.label === query ? this.expansion : null;
		if (!expansion) {
			this.lastSearch = query;
		}
		return from(
			this.graphqlService.getAddressSearch({
				addressSearch: expansion?.search ?? query,
				selected: expansion?.selected,
				companyId: this.companyId,
				countryId: this.countryId,
				zipCode: this.zipCode,
			}),
		).pipe(
			map(r => (r.data?.addressSearch || []).map(x => new AddressSuggestion(x))),
			catchError(() => of([] as AddressSuggestion[])),
			tap(rows => {
				this.suggestions = rows;
			}),
		);
	}
}
